import React from 'react'
import { useIntl } from 'react-intl'
import { Grid, ListItemText, TextField, FormHelperText } from '@mui/material';

import { withStyles } from '@mui/styles'

import { SelectInput } from '../../../ui'
import { gridRowStyles } from './grid-row.styles'

const ROWS_MAX = 4

/**
 * @typedef {object} GridRowItem
 * @property {string} label - El ID del mensaje de i18n para la etiqueta del campo.
 * @property {string} name - El nombre del campo, usado para identificarlo en eventos y errores.
 * @property {string} value - La clave para acceder al valor del campo en el objeto `data`.
 * @property {'input' | 'select' | 'text_area' | 'combo'} [type] - El tipo de campo a renderizar.
 * @property {'numeric'} [subtype] - El subtipo del campo (solo para `type='input'`).
 * @property {string} [comboId] - El ID del combo en el objeto `combos` (requerido para `type='select'` o `type='combo'`).
 * @property {boolean} [disabled] - Si es `true`, el campo estará deshabilitado.
 * @property {function(object): boolean} [disabledByApp] - Una función que recibe `data` y devuelve `true` si el campo debe estar deshabilitado.
 * @property {function(object, object): string} [valueParsed] - Una función que recibe `data` e `intl` y devuelve el valor formateado a mostrar.
 * @property {boolean} [multiline] - Si es `true`, el campo de texto será multilínea (solo para `type='text_area'`).
 * @property {boolean} [fullWidth] - Si es `true`, el campo ocupará todo el ancho disponible.
 */

/**
 * @typedef {object} GridRowComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {GridRowItem[]} [items=[]] - Un array de objetos que definen los campos a renderizar en la fila.
 * @property {object} data - Los datos del formulario o registro actual.
 * @property {number} [spacing=4] - El espaciado entre elementos del Grid de Material-UI.
 * @property {'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'} [justify='flex-start'] - La alineación horizontal de los elementos.
 * @property {number} [rowsMax=4] - El número máximo de filas para campos de texto multilínea.
 * @property {function(React.ChangeEvent): void} onChange - La función callback que se ejecuta cuando un campo cambia (onBlur o Enter).
 * @property {object} [combos] - Un objeto que contiene los datos de combos/opciones para campos de tipo select.
 * @property {Object.<string, string>} [alert] - Un objeto que mapea nombres de campo a mensajes de error.
 * @property {boolean} [disabled] - Si es `true`, todos los campos estarán deshabilitados.
 * @property {React.ReactNode} [append] - Contenido adicional a renderizar al final de la fila.
 */

/**
 * Un componente de fila de formulario flexible que renderiza múltiples campos en un Grid de Material-UI.
 * 
 * Soporta diferentes tipos de campos (input, select, text_area) y permite personalizar el comportamiento
 * de cada campo mediante funciones. Los campos se actualizan en `onBlur` o al presionar Enter.
 * Utiliza `react-intl` para la internacionalización de las etiquetas.
 * 
 * @component
 * @example
 * const formData = {
 *   firstName: 'Juan',
 *   lastName: 'Pérez',
 *   country: 'ES',
 *   notes: 'Notas adicionales',
 * };
 * 
 * const combos = {
 *   countries: {
 *     data: [
 *       { key: 'ES', value: 'España' },
 *       { key: 'FR', value: 'Francia' },
 *     ],
 *   },
 * };
 * 
 * const items = [
 *   { label: 'form.firstName', name: 'firstName', value: 'firstName', type: 'input' },
 *   { label: 'form.lastName', name: 'lastName', value: 'lastName', type: 'input' },
 *   { label: 'form.country', name: 'country', value: 'country', type: 'select', comboId: 'countries' },
 *   { label: 'form.notes', name: 'notes', value: 'notes', type: 'text_area', multiline: true, fullWidth: true },
 * ];
 * 
 * const handleChange = (event) => {
 *   console.log(`Campo ${event.target.name} cambió a: ${event.target.value}`);
 * };
 * 
 * // Este componente debe estar envuelto en un <IntlProvider>.
 * <GridRowComponent
 *   items={items}
 *   data={formData}
 *   combos={combos}
 *   onChange={handleChange}
 *   alert={{ firstName: 'Este campo es requerido' }}
 * />
 * 
 * @param {GridRowComponentProps} props - Las props del componente GridRowComponent.
 */
export const GridRowComponent = withStyles(gridRowStyles)(
  ({
    classes,
    items = [],
    data,
    spacing = 4,
    justify = 'flex-start',
    rowsMax = ROWS_MAX,
    ...props
  }) => {
    const intl = useIntl()

    const handleEnterKey = (event) => {
      if (event.keyCode === 13) {
        props.onChange(event)
      }
    }

    const getValueByType = (item, data) => {
      if (data[item.value] === null) return '--'
      if (item.type === 'combo') {
        let selectedOption =
          props.combos &&
          props.combos[item.comboId] &&
          props.combos[item.comboId].data &&
          props.combos[item.comboId].data.length !== 0 &&
          props.combos[item.comboId].data.find((o) => o.key === data[item.value])
        if (!selectedOption) {
          selectedOption = {}
        }
        return selectedOption.value || data[item.value]
      } else return data[item.value]
    }

    return (
      <Grid container spacing={spacing} justifyContent={justify}>
        {items.map((item, i) => {
          const error = props.alert ? props.alert[item.name] : null
          return (
            <Grid item key={i} style={item.fullWidth ? { flex: '1' } : {}}>
              {/* I wanted to add className={classes.text} somewhere to align the "props" to the right, but it's not obvious how to do it and there's no time for small nuisances*/}
              <ListItemText
                style={{ whiteSpace: 'pre-line' }}
                primary={intl.formatMessage({ id: item.label })}
                secondary={
                  <span>
                    {item.type === 'input' ? (
                      <TextField
                        type={item.subtype === 'numeric' ? 'number' : 'text'}
                        id={item.name}
                        name={item.name}
                        variant="outlined"
                        defaultValue={
                          item.valueParsed ? item.valueParsed(data, intl) : data[item.value]
                        }
                        onBlur={props.onChange}
                        onKeyDown={handleEnterKey}
                        size="small"
                        disabled={
                          item.disabledByApp
                            ? item.disabledByApp(data)
                            : props.disabled || item.disabled
                        }
                      />
                    ) : item.type === 'select' ? (
                      <SelectInput
                        name={item.name}
                        label={item.label}
                        value={data[item.name]}
                        onChange={props.onChange}
                        values={
                          props.combos && props.combos[item.comboId]
                            ? props.combos[item.comboId].data
                            : []
                        }
                        disabled={
                          item.disabledByApp
                            ? item.disabledByApp(data)
                            : props.disabled || item.disabled
                        }
                        // loading={!props.combos[item.value] || props.combos[item.value].isLoading}
                      />
                    ) : item.type === 'text_area' ? (
                      <TextField
                        maxRows={rowsMax}
                        fullWidth={item.fullWidth}
                        type="text"
                        name={item.name}
                        variant="outlined"
                        defaultValue={
                          item.valueParsed
                            ? item.valueParsed(data, intl)
                            : data[item.value] && data[item.value] !== ''
                            ? data[item.value]
                            : '--'
                        }
                        onBlur={props.onChange}
                        onKeyDown={handleEnterKey}
                        disabled={item.disabled}
                        multiline={item.multiline}
                        style={{ flex: '1' }}
                      />
                    ) : item.valueParsed ? (
                      item.valueParsed(data, intl) || '--'
                    ) : (
                      getValueByType(item, data)
                    )}
                  </span>
                }
                primaryTypographyProps={{ variant: 'subtitle2', component: 'span' }}
                secondaryTypographyProps={{ variant: 'body2', component: 'span' }}
              />
              {!!error ? (
                <FormHelperText id="my-helper-text" error={!!error}>
                  {error}
                </FormHelperText>
              ) : null}
            </Grid>
          )
        })}
        {props.append ? props.append : null}
      </Grid>
    )
  }
)
